import { useInfiniteQuery } from "react-query";
import axios from "axios";
import { nuclearn_api_url } from "src/App";
import { SEARCH_STALE_TIME } from "src/features/globalSearch/constants/globalSearch.constants";
import type { ConversationHistoryResponse } from "./types";

// Backend bounds `limit` at 100 and defaults to 25; one screenful per fetch
// keeps the load-more affordance meaningful for long histories.
export const HISTORY_PAGE_SIZE = 25;

/**
 * The endpoint declares `skip` as `le=MAX_SEARCH_OFFSET` (1000), so asking
 * past it is a 422 — and `retryUnlessClientError` deliberately does not retry
 * a 4xx. Stopping here turns a hard error at the bottom of a long history
 * into the list simply ending.
 */
export const HISTORY_MAX_SKIP = 1000;

/**
 * The product slug this surface lists, sent as `?product=`.
 *
 * Pinned rather than read from `ChatStore.productConfig`: the modal only knows
 * how to build assistant and project-chat URLs, so listing another product's
 * dialogues would route them into the wrong shell. A genuinely cross-product
 * history needs a product discriminator on the response and the per-product
 * builders in `buildChatUrlRegistry` — see MRE #30733.
 *
 * The backend resolves the slug to `Dialogues.product_fk`, and for
 * `atom-assist` also admits `product_fk IS NULL` so legacy dialogues written
 * before the column was populated still list.
 */
export const HISTORY_PRODUCT_SLUG = "atom-assist";

export const getConversationHistoryPage = (
  searchQuery: string,
  skip: number,
  inProject?: boolean,
): Promise<ConversationHistoryResponse> => {
  const params: Record<string, string | number | boolean> = {
    skip,
    limit: HISTORY_PAGE_SIZE,
    product: HISTORY_PRODUCT_SLUG,
  };
  const query = searchQuery.trim();
  if (query) params.search_query = query;
  if (inProject !== undefined) params.in_project = inProject;
  return axios
    .get(`${nuclearn_api_url}/chat/dialogue/history`, { params })
    .then((res) => res.data);
};

/**
 * Paginated, searchable listing of the user's own conversations
 * (GET /chat/dialogue/history). An empty query lists by recency; a non-empty
 * one searches title + message text server-side — the endpoint only ever
 * returns the requesting user's dialogues, so no client filtering is needed.
 * `inProject`, when given, scopes to either the "In projects" or "General" tab
 * — a separate cache entry per scope (it's part of the query key), so switching
 * tabs doesn't lose or refetch a scope already paged through. Returns the
 * flattened items loaded so far plus
 * `fetchNextPage`/`hasNextPage`/`isFetchingNextPage` to drive a load-more
 * affordance, `total` for the full count, and `searchBackend` reporting
 * which search path served the request.
 */
export const useConversationHistory = (
  searchQuery: string,
  enabled: boolean = true,
  inProject?: boolean,
) => {
  const query = useInfiniteQuery(
    ["conversation-history", searchQuery.trim(), inProject],
    ({ pageParam = 0 }) =>
      getConversationHistoryPage(searchQuery, pageParam, inProject),
    {
      enabled,
      refetchOnWindowFocus: false,
      // Keep the previous result list rendered while a retyped query loads,
      // so the surface doesn't flash empty on every keystroke. inProject is
      // part of the query key above, so this would also bridge the *other*
      // scope's rows in for the whole in-flight window after a tab switch —
      // a flag that only suppresses it for the render where the key changes
      // doesn't hold (react-query v3 re-reads this option every render).
      // Fixed at the call site instead: mount this hook inside a component
      // keyed by inProject, so a scope switch is a real unmount/remount with
      // no previous observer result to bridge from — see
      // ConversationHistoryResultsPanel.
      keepPreviousData: true,
      staleTime: SEARCH_STALE_TIME,
      // One attempt, like useGlobalSearch: this is a full-text query behind a
      // debounce, so a degraded index would otherwise cost three backed-off
      // requests per pause instead of one.
      retry: 1,
      // The offset the server consumed, not the rows it returned. Postgres
      // drops ids the index still holds (index/DB drift is an expected steady
      // state), so a 25-id window can resolve to fewer rows — paging on rows
      // rendered would re-request hits already on screen and duplicate them.
      //
      // A short page is also the reliable end-of-list signal here: `total` is
      // Manticore's `hits.total` on the search path, which is capped at
      // `offset + limit`, so it cannot be compared against a running count.
      getNextPageParam: (lastPage, pages) => {
        if (lastPage.items.length < HISTORY_PAGE_SIZE) return undefined;
        const nextSkip = pages.length * HISTORY_PAGE_SIZE;
        return nextSkip <= HISTORY_MAX_SKIP ? nextSkip : undefined;
      },
    },
  );

  const items = query.data?.pages.flatMap((p) => p.items) ?? [];
  // Reported, not trusted as a bound: on the search path this is Manticore's
  // capped `hits.total`, so it can equal the rows already loaded while more
  // pages remain. `hasNextPage` is what decides whether to offer more.
  const total = query.data?.pages[0]?.total ?? 0;
  const searchBackend = query.data?.pages[0]?.search_backend ?? null;
  // A next-page failure flips the whole infinite query to `error` while
  // keeping `data`. Split the two so a failed Load more doesn't unmount every
  // page the user already has.
  const isInitialError = query.isError && items.length === 0;
  const isPageError = query.isError && items.length > 0;
  // Derived from the same `query.data` the rows come from, deliberately not
  // from `hasNextPage`. With keepPreviousData, react-query v3 substitutes the
  // previous key's `data` but computes `hasNextPage` from the new key's raw
  // state — so mid-search it reads false while the previous page's rows are
  // still on screen, and the affordance vanishes under the pointer.
  const pages = query.data?.pages ?? [];
  const lastPage = pages[pages.length - 1];
  const canLoadMore =
    lastPage != null &&
    lastPage.items.length === HISTORY_PAGE_SIZE &&
    pages.length * HISTORY_PAGE_SIZE <= HISTORY_MAX_SKIP;
  return {
    ...query,
    items,
    total,
    searchBackend,
    isInitialError,
    isPageError,
    canLoadMore,
  };
};
